import React, { useMemo, useState } from 'react';

const GROUP_OPTIONS = [
  { key: 'candidates', label: 'Candidates' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'clients', label: 'Clients' },
  { key: 'contacts', label: 'Contacts' },
];

const GROUP_DEFS = {
  candidates: {
    label: 'Candidates',
    sourceModule: 'Zoho Candidate Export',
    targetModule: 'JobDiva Candidates',
    description: 'Upload one or more Zoho Candidate CSV exports, then optionally add candidate notes, attachment metadata, attachment categories, and all ExportAttachments ZIP files.',
    supportsAttachments: true,
    targetFields: [
      { key: 'zohoCandidateId', label: 'Zoho Candidate Record ID' },
      { key: 'candidateReference', label: 'Candidate Reference ID' },
      { key: 'candidateName', label: 'Candidate Name' },
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'education', label: 'Education' },
      { key: 'skills', label: 'Skills' },
      { key: 'source', label: 'Source' },
    ],
    exactAliases: {
      'Candidate Id': 'zohoCandidateId',
      'Candidate ID': 'candidateReference',
    },
    aliases: {
      candidate_name: 'candidateName',
      candidate: 'candidateName',
      full_name: 'candidateName',
      fullname: 'candidateName',
      first_name: 'firstName',
      firstname: 'firstName',
      last_name: 'lastName',
      lastname: 'lastName',
      email: 'email',
      mobile: 'phone',
      phone: 'phone',
      current_city: 'city',
      city: 'city',
      state_province: 'state',
      state: 'state',
      highest_qualification_held: 'education',
      qualification: 'education',
      education: 'education',
      skill_set: 'skills',
      skills: 'skills',
      source: 'source',
    },
  },
  jobs: {
    label: 'Jobs',
    sourceModule: 'Zoho Job Opening Export',
    targetModule: 'JobDiva Jobs',
    description: 'Upload one or more Zoho Job Opening CSV exports and optional Notes CSV exports.',
    supportsAttachments: false,
    targetFields: [
      { key: 'referenceNumber', label: 'Reference #' },
      { key: 'title', label: 'Title' },
      { key: 'clientName', label: 'Client' },
      { key: 'department', label: 'Department' },
      { key: 'location', label: 'Location' },
      { key: 'status', label: 'Status' },
      { key: 'description', label: 'Description' },
      { key: 'issueDate', label: 'Issue Date' },
    ],
    aliases: {
      job_opening_id: 'referenceNumber',
      job_id: 'referenceNumber',
      reference: 'referenceNumber',
      job_opening_name: 'title',
      title: 'title',
      client_name: 'clientName',
      client: 'clientName',
      department: 'department',
      city: 'location',
      location: 'location',
      job_status: 'status',
      status: 'status',
      job_description: 'description',
      description: 'description',
      date_opened: 'issueDate',
      opened_date: 'issueDate',
      issue_date: 'issueDate',
    },
  },
  clients: {
    label: 'Clients',
    sourceModule: 'Zoho Client Export',
    targetModule: 'JobDiva Clients',
    description: 'Upload one or more Zoho Client CSV exports and optional Notes CSV exports.',
    supportsAttachments: false,
    targetFields: [
      { key: 'clientName', label: 'Client Name' },
      { key: 'website', label: 'Website' },
      { key: 'phone', label: 'Phone' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'industry', label: 'Industry' },
      { key: 'primaryContact', label: 'Primary Contact' },
    ],
    aliases: {
      account_name: 'clientName',
      client_name: 'clientName',
      company: 'clientName',
      name: 'clientName',
      website: 'website',
      phone: 'phone',
      billing_city: 'city',
      city: 'city',
      billing_state: 'state',
      state: 'state',
      industry: 'industry',
      primary_contact: 'primaryContact',
      contact: 'primaryContact',
    },
  },
  contacts: {
    label: 'Contacts',
    sourceModule: 'Zoho Contact Export',
    targetModule: 'JobDiva Contacts',
    description: 'Upload one or more Zoho Contact CSV exports, optional Notes CSV exports, and contact attachment exports. Contact relationship rules will be finalized after the Candidate workflow.',
    supportsAttachments: true,
    targetFields: [
      { key: 'contactReference', label: 'Contact Reference ID' },
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name' },
      { key: 'clientName', label: 'Client' },
      { key: 'title', label: 'Title' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'mobile', label: 'Mobile' },
    ],
    aliases: {
      contact_id: 'contactReference',
      first_name: 'firstName',
      last_name: 'lastName',
      client_name: 'clientName',
      account_name: 'clientName',
      title: 'title',
      email: 'email',
      phone: 'phone',
      mobile: 'mobile',
    },
  },
};

const createGroupData = () => ({
  sourceFiles: [],
  noteFiles: [],
  attachmentFiles: [],
  categoryFiles: [],
  zipFiles: [],
  headers: [],
  rows: [],
  notes: [],
  attachments: [],
  categories: [],
  zipIndex: new Map(),
  mapping: {},
  preview: [],
  analysis: null,
});

const createInitialGroupData = () => Object.fromEntries(
  GROUP_OPTIONS.map(({ key }) => [key, createGroupData()])
);

const CSV_KIND_CONFIG = {
  source: { filesKey: 'sourceFiles', rowsKey: 'rows' },
  notes: { filesKey: 'noteFiles', rowsKey: 'notes' },
  attachments: { filesKey: 'attachmentFiles', rowsKey: 'attachments' },
  categories: { filesKey: 'categoryFiles', rowsKey: 'categories' },
};

const normalizeHeader = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
const normalizeFileName = (value) => String(value || '').trim().replace(/\\/g, '/').split('/').pop().toLowerCase();

const parseCsvRows = (rawText) => {
  const text = String(rawText || '').replace(/^\uFEFF/, '');
  const rows = [];
  let current = '';
  let row = [];
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      row.push(current);
      current = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') index += 1;
      row.push(current);
      if (row.some((cell) => String(cell).trim() !== '')) rows.push(row);
      row = [];
      current = '';
      continue;
    }

    current += char;
  }

  if (inQuotes) throw new Error('The CSV contains an unterminated quoted value.');

  if (current.length > 0 || row.length > 0) {
    row.push(current);
    if (row.some((cell) => String(cell).trim() !== '')) rows.push(row);
  }

  return rows;
};

const parseCsvFile = async (file) => {
  const parsedRows = parseCsvRows(await file.text());
  if (parsedRows.length < 2) {
    throw new Error(`${file.name} is empty or missing a header row.`);
  }

  const headers = parsedRows[0].map((header) => String(header).trim());
  const duplicateHeaders = headers.filter((header, index) => headers.indexOf(header) !== index);
  if (duplicateHeaders.length > 0) {
    throw new Error(`${file.name} contains duplicate headers: ${[...new Set(duplicateHeaders)].join(', ')}.`);
  }

  const rows = parsedRows.slice(1).map((row) => {
    const item = { __sourceFile: file.name };
    headers.forEach((header, index) => {
      item[header] = row[index] ?? '';
    });
    return item;
  });

  return {
    file: { name: file.name, size: file.size, rowCount: rows.length },
    headers,
    rows,
  };
};

const decodeZipFileName = (bytes) => new TextDecoder('utf-8').decode(bytes);

const readZipEntries = async (file) => {
  const tailLength = Math.min(file.size, 65_557);
  const tailOffset = file.size - tailLength;
  const tailBuffer = await file.slice(tailOffset).arrayBuffer();
  const tail = new DataView(tailBuffer);
  let endOffset = -1;

  for (let offset = tail.byteLength - 22; offset >= 0; offset -= 1) {
    if (tail.getUint32(offset, true) === 0x06054b50) {
      endOffset = offset;
      break;
    }
  }

  if (endOffset < 0) throw new Error(`${file.name} is not a readable ZIP archive.`);

  const totalEntries = tail.getUint16(endOffset + 10, true);
  const directorySize = tail.getUint32(endOffset + 12, true);
  const directoryOffset = tail.getUint32(endOffset + 16, true);
  if (totalEntries === 0xffff || directorySize === 0xffffffff || directoryOffset === 0xffffffff) {
    throw new Error(`${file.name} uses ZIP64, which is not supported by this browser preview.`);
  }

  const directoryBuffer = await file.slice(directoryOffset, directoryOffset + directorySize).arrayBuffer();
  const directory = new DataView(directoryBuffer);
  const bytes = new Uint8Array(directoryBuffer);
  const entries = [];
  let offset = 0;

  while (offset + 46 <= directory.byteLength && entries.length < totalEntries) {
    if (directory.getUint32(offset, true) !== 0x02014b50) {
      throw new Error(`${file.name} has an invalid ZIP directory.`);
    }

    const fileNameLength = directory.getUint16(offset + 28, true);
    const extraLength = directory.getUint16(offset + 30, true);
    const commentLength = directory.getUint16(offset + 32, true);
    const nameStart = offset + 46;
    const entryName = decodeZipFileName(bytes.slice(nameStart, nameStart + fileNameLength));
    if (entryName && !entryName.endsWith('/')) entries.push(entryName);
    offset = nameStart + fileNameLength + extraLength + commentLength;
  }

  return entries;
};

const displayValue = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') return value.name || value.display_value || value.id || JSON.stringify(value);
  return String(value);
};

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / (1024 ** unitIndex)).toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const transformPreviewValue = (targetKey, value) => {
  const stringValue = value == null ? '' : String(value).trim();
  if (!stringValue) return null;

  switch (targetKey) {
    case 'email':
      return stringValue.toLowerCase();
    case 'phone':
    case 'mobile':
      return stringValue.replace(/[^\d+]/g, '').replace(/^\+?1(?=\d{10}$)/, '+1');
    case 'description':
      return stringValue.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    case 'firstName':
      return stringValue.split(/\s+/).filter(Boolean)[0] || stringValue;
    case 'lastName':
      return stringValue.split(/\s+/).filter(Boolean).slice(-1)[0] || stringValue;
    case 'status':
      return stringValue.toLowerCase();
    default:
      return stringValue;
  }
};

const guessTargetForSource = (sourceField, groupKey) => {
  const definition = GROUP_DEFS[groupKey];
  if (definition.exactAliases?.[sourceField]) return definition.exactAliases[sourceField];

  const normalized = normalizeHeader(sourceField);
  const alias = Object.entries(definition.aliases || {})
    .find(([sourceAlias]) => normalizeHeader(sourceAlias) === normalized);
  return alias?.[1] || null;
};

const addGuessedMappings = (mapping, headers, groupKey) => {
  const nextMapping = { ...mapping };
  headers.forEach((header) => {
    if (Object.prototype.hasOwnProperty.call(nextMapping, header)) return;
    const guessed = guessTargetForSource(header, groupKey);
    if (guessed) nextMapping[header] = guessed;
  });
  return nextMapping;
};

const findHeader = (headers, preferredNames) => {
  for (const preferred of preferredNames) {
    const exact = headers.find((header) => header === preferred);
    if (exact) return exact;
  }
  const normalizedNames = new Set(preferredNames.map(normalizeHeader));
  return headers.find((header) => normalizedNames.has(normalizeHeader(header))) || null;
};

const analyzeCandidateBundle = (data, mappedRows) => {
  const candidateIdHeader = findHeader(data.headers, ['Candidate Id', 'Candidate Record Id']);
  if (!candidateIdHeader) {
    throw new Error('Candidate CSV must contain a "Candidate Id" column so notes and attachments can be linked.');
  }

  const candidateIds = new Set();
  const duplicateCandidateIds = new Set();
  let candidatesMissingIds = 0;
  data.rows.forEach((row) => {
    const id = String(row[candidateIdHeader] || '').trim();
    if (!id) {
      candidatesMissingIds += 1;
      return;
    }
    if (candidateIds.has(id)) duplicateCandidateIds.add(id);
    candidateIds.add(id);
  });

  const noteHeaders = data.notes.length > 0 ? Object.keys(data.notes[0]) : [];
  const noteParentHeader = findHeader(noteHeaders, ['Parent ID', 'Candidate Id']);
  const noteModuleHeader = findHeader(noteHeaders, ['Module']);
  if (data.notes.length > 0 && !noteParentHeader) {
    throw new Error('Notes CSV must contain "Parent ID" or "Candidate Id" so notes can be linked.');
  }
  const noteCounts = new Map();
  let candidateNotes = 0;
  let skippedNotes = 0;
  let orphanNotes = 0;

  data.notes.forEach((note) => {
    const moduleName = noteModuleHeader ? String(note[noteModuleHeader] || '').trim().toLowerCase() : 'candidates';
    if (moduleName && moduleName !== 'candidates') {
      skippedNotes += 1;
      return;
    }
    candidateNotes += 1;
    const candidateId = noteParentHeader ? String(note[noteParentHeader] || '').trim() : '';
    if (!candidateId || !candidateIds.has(candidateId)) {
      orphanNotes += 1;
      return;
    }
    noteCounts.set(candidateId, (noteCounts.get(candidateId) || 0) + 1);
  });

  const attachmentHeaders = data.attachments.length > 0 ? Object.keys(data.attachments[0]) : [];
  const attachmentParentHeader = findHeader(attachmentHeaders, ['Parent ID']);
  const attachmentFileHeader = findHeader(attachmentHeaders, ['File Name']);
  const attachmentCategoryHeader = findHeader(attachmentHeaders, ['Attachment Category Id']);
  const categoryHeaders = data.categories.length > 0 ? Object.keys(data.categories[0]) : [];
  const categoryIdHeader = findHeader(categoryHeaders, ['Attachment Category Id']);
  const categoryLabelHeader = findHeader(categoryHeaders, ['Label']);
  const categoryLookup = new Map();
  if (data.attachments.length > 0 && (!attachmentParentHeader || !attachmentFileHeader)) {
    throw new Error('Attachments CSV must contain both "Parent ID" and "File Name" columns.');
  }

  data.categories.forEach((category) => {
    const id = categoryIdHeader ? String(category[categoryIdHeader] || '').trim() : '';
    if (id) categoryLookup.set(id, categoryLabelHeader ? String(category[categoryLabelHeader] || '').trim() : id);
  });

  const attachmentStats = new Map();
  let orphanAttachments = 0;
  let resolvedAttachments = 0;
  let missingAttachmentFiles = 0;
  let ambiguousAttachmentFiles = 0;
  const missingFileNames = [];

  data.attachments.forEach((attachment) => {
    const candidateId = attachmentParentHeader ? String(attachment[attachmentParentHeader] || '').trim() : '';
    const fileName = attachmentFileHeader ? String(attachment[attachmentFileHeader] || '').trim() : '';
    const matches = data.zipIndex.get(normalizeFileName(fileName)) || [];
    const categoryId = attachmentCategoryHeader ? String(attachment[attachmentCategoryHeader] || '').trim() : '';
    const category = categoryLookup.get(categoryId) || String(attachment.Category || '').trim() || 'Uncategorized';

    if (!candidateId || !candidateIds.has(candidateId)) {
      orphanAttachments += 1;
      return;
    }

    const stats = attachmentStats.get(candidateId) || {
      total: 0,
      resolved: 0,
      missing: 0,
      ambiguous: 0,
      categories: new Set(),
    };
    stats.total += 1;
    stats.categories.add(category);

    if (matches.length === 1) {
      stats.resolved += 1;
      resolvedAttachments += 1;
    } else if (matches.length > 1) {
      stats.ambiguous += 1;
      ambiguousAttachmentFiles += 1;
    } else {
      stats.missing += 1;
      missingAttachmentFiles += 1;
      if (missingFileNames.length < 20) missingFileNames.push(fileName || '(blank file name)');
    }
    attachmentStats.set(candidateId, stats);
  });

  const preview = mappedRows.map((mapped, index) => {
    const candidateId = String(data.rows[index]?.[candidateIdHeader] || '').trim();
    const stats = attachmentStats.get(candidateId);
    return {
      ...mapped,
      __candidateId: candidateId,
      __noteCount: noteCounts.get(candidateId) || 0,
      __attachmentCount: stats?.total || 0,
      __resolvedAttachmentCount: stats?.resolved || 0,
      __missingAttachmentCount: stats?.missing || 0,
      __attachmentCategories: stats ? [...stats.categories].join(', ') : '',
    };
  });

  return {
    preview,
    analysis: {
      candidateIdHeader,
      candidateCount: data.rows.length,
      candidateIdCount: candidateIds.size,
      candidatesMissingIds,
      duplicateCandidateIds: duplicateCandidateIds.size,
      candidateNotes,
      linkedNotes: candidateNotes - orphanNotes,
      skippedNotes,
      orphanNotes,
      attachmentCount: data.attachments.length,
      linkedAttachments: data.attachments.length - orphanAttachments,
      orphanAttachments,
      resolvedAttachments,
      missingAttachmentFiles,
      ambiguousAttachmentFiles,
      archiveCount: data.zipFiles.length,
      indexedArchiveFiles: data.zipIndex.size,
      missingFileNames,
    },
  };
};

const FileList = ({ files }) => {
  if (files.length === 0) return <span className="admin-jobdiva-subtle">No files selected</span>;
  return (
    <ul className="admin-etl-file-list">
      {files.map((file) => (
        <li key={`${file.name}-${file.size}`}>
          <strong>{file.name}</strong>
          <span>{file.rowCount != null ? `${file.rowCount.toLocaleString()} rows` : `${file.entryCount.toLocaleString()} files`} · {formatBytes(file.size)}</span>
        </li>
      ))}
    </ul>
  );
};

const UploadField = ({ id, label, description, accept, multiple = true, files, disabled, onChange }) => (
  <div className="admin-etl-upload-card">
    <label htmlFor={id}>
      <strong>{label}</strong>
      <span>{description}</span>
    </label>
    <input id={id} type="file" accept={accept} multiple={multiple} disabled={disabled} onChange={onChange} />
    <FileList files={files} />
  </div>
);

const ZohoJobDivaEtlMap = () => {
  const [activeGroup, setActiveGroup] = useState('candidates');
  const [groupData, setGroupData] = useState(createInitialGroupData);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const activeDefinition = GROUP_DEFS[activeGroup];
  const activeData = groupData[activeGroup];
  const targetOptions = useMemo(() => activeDefinition.targetFields, [activeDefinition]);
  const mappedHeaderCount = Object.keys(activeData.mapping).length;

  const addCsvFiles = async (kind, event) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    if (files.length === 0) return;

    setLoadingFiles(true);
    setUploadError('');
    try {
      const parsed = await Promise.all(files.map(parseCsvFile));
      setGroupData((current) => {
        const entry = current[activeGroup];
        const { filesKey, rowsKey } = CSV_KIND_CONFIG[kind];
        const existingFiles = entry[filesKey];
        const existingKeys = new Set(existingFiles.map((file) => `${file.name}:${file.size}`));
        const additions = parsed.filter(({ file }) => !existingKeys.has(`${file.name}:${file.size}`));
        if (additions.length === 0) return current;

        const nextFiles = [...existingFiles, ...additions.map(({ file }) => file)];
        const nextRows = [...entry[rowsKey], ...additions.flatMap(({ rows }) => rows)];
        const nextEntry = {
          ...entry,
          [filesKey]: nextFiles,
          [rowsKey]: nextRows,
          preview: [],
          analysis: null,
        };

        if (kind === 'source') {
          const headers = [...new Set([...entry.headers, ...additions.flatMap(({ headers: addedHeaders }) => addedHeaders)])];
          nextEntry.headers = headers;
          nextEntry.mapping = addGuessedMappings(entry.mapping, headers, activeGroup);
        }

        return { ...current, [activeGroup]: nextEntry };
      });
    } catch (error) {
      setUploadError(error.message || 'Unable to read the selected CSV files.');
    } finally {
      input.value = '';
      setLoadingFiles(false);
    }
  };

  const addZipFiles = async (event) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    if (files.length === 0) return;

    setLoadingFiles(true);
    setUploadError('');
    try {
      const indexed = await Promise.all(files.map(async (file) => ({
        file: { name: file.name, size: file.size },
        entries: await readZipEntries(file),
      })));

      setGroupData((current) => {
        const entry = current[activeGroup];
        const existingKeys = new Set(entry.zipFiles.map((file) => `${file.name}:${file.size}`));
        const additions = indexed.filter(({ file }) => !existingKeys.has(`${file.name}:${file.size}`));
        if (additions.length === 0) return current;

        const zipIndex = new Map(entry.zipIndex);
        additions.forEach(({ file, entries }) => {
          entries.forEach((entryName) => {
            const key = normalizeFileName(entryName);
            if (!key) return;
            zipIndex.set(key, [...(zipIndex.get(key) || []), { archiveName: file.name, entryName }]);
          });
        });

        return {
          ...current,
          [activeGroup]: {
            ...entry,
            zipFiles: [
              ...entry.zipFiles,
              ...additions.map(({ file, entries }) => ({ ...file, entryCount: entries.length })),
            ],
            zipIndex,
            preview: [],
            analysis: null,
          },
        };
      });
    } catch (error) {
      setUploadError(error.message || 'Unable to index the selected ZIP files.');
    } finally {
      input.value = '';
      setLoadingFiles(false);
    }
  };

  const updateMapping = (sourceField, nextValue) => {
    setGroupData((current) => {
      const entry = current[activeGroup];
      const mapping = { ...entry.mapping };
      if (nextValue) mapping[sourceField] = nextValue;
      else delete mapping[sourceField];
      return {
        ...current,
        [activeGroup]: { ...entry, mapping, preview: [], analysis: null },
      };
    });
  };

  const resetActiveGroup = () => {
    setGroupData((current) => ({ ...current, [activeGroup]: createGroupData() }));
    setUploadError('');
  };

  const runPreview = async () => {
    setLoadingPreview(true);
    setUploadError('');
    try {
      const mappedRows = activeData.rows.slice(0, 10).map((row) => {
        const mapped = {};
        activeData.headers.forEach((header) => {
          const targetKey = activeData.mapping[header];
          if (targetKey) mapped[targetKey] = transformPreviewValue(targetKey, row[header]);
        });
        return mapped;
      });

      const result = activeGroup === 'candidates'
        ? analyzeCandidateBundle(activeData, mappedRows)
        : { preview: mappedRows, analysis: null };

      setGroupData((current) => ({
        ...current,
        [activeGroup]: {
          ...current[activeGroup],
          preview: result.preview,
          analysis: result.analysis,
        },
      }));
    } catch (error) {
      setUploadError(error.message || 'Unable to generate the preview.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const analysis = activeData.analysis;

  return (
    <div className="admin-jobdiva-card" style={{ marginTop: 16 }}>
      <div className="admin-etl-heading">
        <div>
          <h3>Zoho CSV → JobDiva ETL Mapping</h3>
          <p className="admin-jobdiva-subtle">
            Build and validate a linked Zoho export bundle. Preview mode reads local files only and never writes to Zoho or JobDiva.
          </p>
        </div>
        <div className="admin-etl-heading-actions">
          <button type="button" className="btn btn-secondary" onClick={resetActiveGroup} disabled={loadingFiles}>
            Clear {activeDefinition.label}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={runPreview}
            disabled={loadingPreview || loadingFiles || activeData.rows.length === 0}
          >
            {loadingPreview ? 'Running preview...' : 'Run preview mapping'}
          </button>
        </div>
      </div>

      <div className="admin-subtabs" style={{ marginTop: 16 }}>
        {GROUP_OPTIONS.map((group) => (
          <button
            key={group.key}
            type="button"
            className={`admin-subtab-btn ${activeGroup === group.key ? 'active' : ''}`}
            onClick={() => {
              setActiveGroup(group.key);
              setUploadError('');
            }}
          >
            {group.label}
          </button>
        ))}
      </div>

      <div className="admin-zoho-etl-banner">
        Preview mode only: files stay in your browser. Large ZIP archives are indexed from their directory without loading or extracting every attachment.
      </div>

      <div className="admin-etl-summary-grid">
        <div className="admin-etl-summary-card">
          <span className="admin-etl-summary-label">Source records</span>
          <strong>{activeData.rows.length.toLocaleString()}</strong>
        </div>
        <div className="admin-etl-summary-card">
          <span className="admin-etl-summary-label">Notes</span>
          <strong>{activeData.notes.length.toLocaleString()}</strong>
        </div>
        <div className="admin-etl-summary-card">
          <span className="admin-etl-summary-label">Attachments</span>
          <strong>{activeData.attachments.length.toLocaleString()}</strong>
        </div>
        <div className="admin-etl-summary-card">
          <span className="admin-etl-summary-label">ZIP archives</span>
          <strong>{activeData.zipFiles.length.toLocaleString()}</strong>
        </div>
      </div>

      <div className="admin-jobdiva-subtle admin-etl-description">{activeDefinition.description}</div>

      <div className="admin-etl-upload-grid">
        <UploadField
          id={`zoho-source-${activeGroup}`}
          label={`${activeDefinition.label} data CSV`}
          description="Required. Select all numbered export CSV files together."
          accept=".csv,text/csv"
          files={activeData.sourceFiles}
          disabled={loadingFiles}
          onChange={(event) => addCsvFiles('source', event)}
        />
        <UploadField
          id={`zoho-notes-${activeGroup}`}
          label="Notes CSV"
          description="Optional. Notes are linked to their parent Zoho record during preview."
          accept=".csv,text/csv"
          files={activeData.noteFiles}
          disabled={loadingFiles}
          onChange={(event) => addCsvFiles('notes', event)}
        />
        {activeDefinition.supportsAttachments && (
          <>
            <UploadField
              id={`zoho-attachments-${activeGroup}`}
              label="Attachments CSV"
              description="Optional attachment metadata containing Parent ID and File Name."
              accept=".csv,text/csv"
              files={activeData.attachmentFiles}
              disabled={loadingFiles}
              onChange={(event) => addCsvFiles('attachments', event)}
            />
            <UploadField
              id={`zoho-categories-${activeGroup}`}
              label="Attachment Category CSV"
              description="Optional lookup used to identify resumes and other document categories."
              accept=".csv,text/csv"
              files={activeData.categoryFiles}
              disabled={loadingFiles}
              onChange={(event) => addCsvFiles('categories', event)}
            />
            <UploadField
              id={`zoho-zips-${activeGroup}`}
              label="ExportAttachments ZIP files"
              description="Optional. Select every ExportAttachments_*.zip archive together."
              accept=".zip,application/zip"
              files={activeData.zipFiles}
              disabled={loadingFiles}
              onChange={addZipFiles}
            />
          </>
        )}
      </div>

      {loadingFiles && <div className="admin-zoho-etl-banner">Reading and indexing selected files…</div>}
      {uploadError && <div className="admin-jobdiva-error-box admin-etl-error">{uploadError}</div>}

      {analysis && (
        <div className="admin-etl-analysis">
          <h4>Candidate bundle validation</h4>
          <div className="admin-etl-summary-grid">
            <div className="admin-etl-summary-card">
              <span className="admin-etl-summary-label">Candidate IDs</span>
              <strong>{analysis.candidateIdCount.toLocaleString()}</strong>
              <small>{analysis.duplicateCandidateIds} duplicate · {analysis.candidatesMissingIds} missing IDs</small>
            </div>
            <div className="admin-etl-summary-card">
              <span className="admin-etl-summary-label">Linked notes</span>
              <strong>{analysis.linkedNotes.toLocaleString()} / {analysis.candidateNotes.toLocaleString()}</strong>
              <small>{analysis.orphanNotes} unmatched · {analysis.skippedNotes} other modules</small>
            </div>
            <div className="admin-etl-summary-card">
              <span className="admin-etl-summary-label">Linked attachments</span>
              <strong>{analysis.linkedAttachments.toLocaleString()} / {analysis.attachmentCount.toLocaleString()}</strong>
              <small>{analysis.orphanAttachments} unmatched parents</small>
            </div>
            <div className={`admin-etl-summary-card ${analysis.missingAttachmentFiles > 0 ? 'warning' : 'success'}`}>
              <span className="admin-etl-summary-label">Resolved files</span>
              <strong>{analysis.resolvedAttachments.toLocaleString()}</strong>
              <small>{analysis.missingAttachmentFiles} missing · {analysis.ambiguousAttachmentFiles} ambiguous</small>
            </div>
          </div>
          {analysis.missingFileNames.length > 0 && (
            <details className="admin-etl-warning-details">
              <summary>Show sample missing attachment files ({Math.min(analysis.missingFileNames.length, 20)} shown)</summary>
              <ul>
                {analysis.missingFileNames.map((fileName) => <li key={fileName}>{fileName}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      {activeData.headers.length > 0 && (
        <>
          <div className="admin-etl-section-heading">
            <div>
              <h4>Field mapping</h4>
              <span>{activeData.headers.length} CSV columns · {mappedHeaderCount} mapped</span>
            </div>
          </div>
          <div className="admin-jobdiva-table-wrap">
            <table className="admin-jobdiva-table admin-zoho-etl-table">
              <thead>
                <tr>
                  <th>CSV column</th>
                  <th>JobDiva field</th>
                  <th>Sample value</th>
                </tr>
              </thead>
              <tbody>
                {activeData.headers.map((header) => (
                  <tr key={`${activeGroup}-${header}`}>
                    <td>{header}</td>
                    <td>
                      <select
                        value={activeData.mapping[header] || ''}
                        onChange={(event) => updateMapping(header, event.target.value)}
                        style={{ width: '100%' }}
                      >
                        <option value="">Not mapped</option>
                        {targetOptions.map((target) => (
                          <option key={target.key} value={target.key}>{target.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>{displayValue(activeData.rows[0]?.[header])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeData.preview.length > 0 && (
        <div className="admin-etl-preview">
          <h4>Preview output (first {activeData.preview.length} records)</h4>
          <div className="admin-jobdiva-table-wrap">
            <table className="admin-jobdiva-table admin-zoho-etl-table">
              <thead>
                <tr>
                  {targetOptions.map((target) => <th key={target.key}>{target.label}</th>)}
                  {activeGroup === 'candidates' && (
                    <>
                      <th>Linked notes</th>
                      <th>Attachments</th>
                      <th>Resolved files</th>
                      <th>Missing files</th>
                      <th>Categories</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {activeData.preview.map((row, index) => (
                  <tr key={`${activeGroup}-preview-${row.__candidateId || index}`}>
                    {targetOptions.map((target) => (
                      <td key={`${activeGroup}-preview-${index}-${target.key}`}>{displayValue(row[target.key])}</td>
                    ))}
                    {activeGroup === 'candidates' && (
                      <>
                        <td>{row.__noteCount}</td>
                        <td>{row.__attachmentCount}</td>
                        <td>{row.__resolvedAttachmentCount}</td>
                        <td>{row.__missingAttachmentCount}</td>
                        <td>{displayValue(row.__attachmentCategories)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZohoJobDivaEtlMap;
